import React, { useCallback, useEffect, useRef, useState } from 'react'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import EmailIcon from '@mui/icons-material/Email'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import LockIcon from '@mui/icons-material/Lock'
import LogoutIcon from '@mui/icons-material/Logout'
import SettingsIcon from '@mui/icons-material/Settings'
import TuneIcon from '@mui/icons-material/Tune'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { Link } from 'react-router'
import DeviceControlsModal from '../components/DeviceControlsModal'
import DeviceLogsModal from '../components/DeviceLogsModal'
import DeviceSettingsModal from '../components/DeviceSettingsModal'
import MainDashboardSidebar from '../components/MainDashboardSidebar'
import TechConfigPage from './TechConfigPage'
import {
  authorizePendingDeviceAssignment,
  clearPendingDeviceAssignment,
  clearLoginSession,
  getBoardInfo,
  getLoginSession,
  getLoginUserRole,
  getProfileDeviceIds,
  getUserDisplayInfo,
  getUserProfile,
  loginUser,
  savePendingDeviceAssignment,
} from '../services/authApi'
import './MainDashboardPage.css'
import './DeviceListPage.css'

function unwrapBoardInfo(boardInfo) {
  if (!boardInfo || typeof boardInfo !== 'object') {
    return {}
  }

  if (boardInfo.success !== undefined && boardInfo.data && typeof boardInfo.data === 'object') {
    return boardInfo.data
  }

  return boardInfo
}

function toNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string') {
    const parsed = Number(value)

    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

function formatPower(value) {
  const number = toNumber(value)

  return number === null ? 'N/A' : number.toFixed(2)
}

function getDeviceRow(deviceId, boardInfo) {
  const payload = unwrapBoardInfo(boardInfo)
  const data = payload.data || {}

  return {
    deviceId: payload.config?.Device?.ID || data.DeviceID || data.deviceId || deviceId,
    powerImport: data.powerImport ?? payload.devicedata?.meter?.import?.W ?? null,
    powerExport: data.powerExport ?? payload.devicedata?.meter?.export?.W ?? null,
  }
}

async function loadDeviceRow(deviceId) {
  try {
    const boardInfo = await getBoardInfo(deviceId)

    return getDeviceRow(deviceId, boardInfo)
  } catch (error) {
    console.warn(`Unable to fetch board info for ${deviceId}:`, error)

    return {
      deviceId,
      powerImport: null,
      powerExport: null,
    }
  }
}

function DeviceListPage({ isDarkMode = false, onSelectTheme }) {
  const [deviceRows, setDeviceRows] = useState([])
  const [userInfo, setUserInfo] = useState(() => getUserDisplayInfo(getLoginSession()?.user))
  const [customerAssignment, setCustomerAssignment] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [logsDeviceId, setLogsDeviceId] = useState('')
  const [controlsDeviceId, setControlsDeviceId] = useState('')
  const [settingsDeviceId, setSettingsDeviceId] = useState('')
  const [isAddDeviceOpen, setIsAddDeviceOpen] = useState(false)
  const [isTechnicianAuthorized, setIsTechnicianAuthorized] = useState(false)
  const [isAuthorizingTechnician, setIsAuthorizingTechnician] = useState(false)
  const [isTechnicianPasswordVisible, setIsTechnicianPasswordVisible] = useState(false)
  const [technicianCredentials, setTechnicianCredentials] = useState({
    email: '',
    password: '',
  })
  const [technicianAuthError, setTechnicianAuthError] = useState('')
  const addDeviceCloseRef = useRef(null)
  const handleOpenLogs = (deviceId) => {
    setLogsDeviceId(deviceId)
  }
  const handleOpenControls = (deviceId) => {
    setControlsDeviceId(deviceId)
  }
  const handleOpenSettings = (deviceId) => {
    setSettingsDeviceId(deviceId)
  }

  useEffect(() => {
    let isMounted = true
    const loginSession = getLoginSession()
    const sessionUser = loginSession?.user || {}

    if (sessionUser.email || sessionUser.name || sessionUser.fullName || sessionUser.firstName) {
      setUserInfo(getUserDisplayInfo(sessionUser))
    }

    if (!sessionUser.email) {
      return () => {
        isMounted = false
      }
    }

    setIsLoading(true)
    setLoadError('')

    getUserProfile(sessionUser.email)
      .then(async (profileResponse) => {
        const profileUser = profileResponse?.user || {}
        const deviceIds = getProfileDeviceIds(profileResponse)

        if (isMounted) {
          setUserInfo(getUserDisplayInfo({ ...sessionUser, ...profileUser }))
          setCustomerAssignment({
            userId: profileUser.userId || profileUser.id || sessionUser.userId || sessionUser.id,
            email: profileUser.email || sessionUser.email || '',
            displayName: getUserDisplayInfo({ ...sessionUser, ...profileUser }).displayName,
            deviceIds,
          })
        }

        if (!deviceIds.length) {
          return []
        }

        return Promise.all(deviceIds.map(loadDeviceRow))
      })
      .then((rows) => {
        if (isMounted) {
          setDeviceRows(rows)
        }
      })
      .catch((error) => {
        console.error('Device list request failed:', error)

        if (isMounted) {
          setDeviceRows([])
          setLoadError('Unable to load devices.')
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    if (!isAddDeviceOpen) {
      return undefined
    }

    const previousOverflow = document.body.style.overflow
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        clearPendingDeviceAssignment()
        setIsAddDeviceOpen(false)
        setIsTechnicianAuthorized(false)
        setTechnicianAuthError('')
        setTechnicianCredentials({
          email: '',
          password: '',
        })
      }
    }

    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', handleKeyDown)
    window.setTimeout(() => addDeviceCloseRef.current?.focus(), 0)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isAddDeviceOpen])

  const handleAddDevice = () => {
    if (!customerAssignment?.userId) {
      setLoadError('Unable to identify the customer account for device assignment.')
      return
    }

    savePendingDeviceAssignment(customerAssignment)
    setTechnicianCredentials({
      email: '',
      password: '',
    })
    setTechnicianAuthError('')
    setIsTechnicianAuthorized(false)
    setIsTechnicianPasswordVisible(false)
    setIsAddDeviceOpen(true)
  }

  const handleCloseAddDevice = () => {
    clearPendingDeviceAssignment()
    setIsAddDeviceOpen(false)
    setIsTechnicianAuthorized(false)
    setTechnicianAuthError('')
    setTechnicianCredentials({
      email: '',
      password: '',
    })
  }

  const handleTechnicianCredentialChange = (event) => {
    const { name, value } = event.target

    setTechnicianCredentials((currentCredentials) => ({
      ...currentCredentials,
      [name]: value,
    }))
  }

  const handleTechnicianAuthorization = async (event) => {
    event.preventDefault()

    if (isAuthorizingTechnician) {
      return
    }

    setIsAuthorizingTechnician(true)
    setTechnicianAuthError('')

    try {
      const loginResponse = await loginUser({
        email: technicianCredentials.email.trim(),
        password: technicianCredentials.password,
      })
      const role = getLoginUserRole(loginResponse)

      if (role !== 'tech' && role !== 'super tech') {
        throw new Error('A technician account is required to add a device.')
      }

      authorizePendingDeviceAssignment()
      setIsTechnicianAuthorized(true)
      setTechnicianCredentials({
        email: '',
        password: '',
      })
    } catch (error) {
      setTechnicianAuthError(error.message || 'Unable to authorize the technician.')
    } finally {
      setIsAuthorizingTechnician(false)
    }
  }

  const handleDeviceAssigned = useCallback((deviceId) => {
    setCustomerAssignment((currentAssignment) => {
      if (!currentAssignment) {
        return currentAssignment
      }

      return {
        ...currentAssignment,
        deviceIds: Array.from(new Set([...(currentAssignment.deviceIds || []), deviceId])),
      }
    })

    loadDeviceRow(deviceId).then((deviceRow) => {
      setDeviceRows((currentRows) => {
        const existingIndex = currentRows.findIndex((row) => row.deviceId === deviceRow.deviceId)

        if (existingIndex === -1) {
          return [...currentRows, deviceRow]
        }

        return currentRows.map((row, index) => index === existingIndex ? deviceRow : row)
      })
    })
  }, [])

  return (
    <main className="dashboard-page device-list-page">
      <section className="dashboard-shell" aria-label="CNERGI device list">
        <MainDashboardSidebar />

        <div className="dashboard-main">
          <header className="dashboard-header">
            <div className="dashboard-header__welcome">
              <span>CNERGI</span>
              <h1>Welcome, {userInfo.firstName}!</h1>
              <p>{userInfo.email}</p>
            </div>

            <div className="dashboard-header__actions">
              <div className="dashboard-theme-switch" role="group" aria-label="Theme selection">
                <button
                  className={`dashboard-theme-choice ${!isDarkMode ? 'dashboard-theme-choice--active' : ''}`}
                  type="button"
                  onClick={() => onSelectTheme?.('light')}
                >
                  <span className="dashboard-theme-choice__swatch" aria-hidden="true"></span>
                  Default
                </button>

                <button
                  className={`dashboard-theme-choice ${isDarkMode ? 'dashboard-theme-choice--active' : ''}`}
                  type="button"
                  onClick={() => onSelectTheme?.('dark')}
                >
                  <span className="dashboard-theme-choice__swatch" aria-hidden="true"></span>
                  Midnight
                </button>
              </div>

              <div className="dashboard-user">
                <AccountCircleIcon aria-hidden="true" />
                <strong>{userInfo.displayName}</strong>
              </div>

              <a className="dashboard-logout" href="/login" onClick={clearLoginSession}>
                <LogoutIcon aria-hidden="true" />
                Logout
              </a>
            </div>
          </header>

          <section className="device-list-panel" aria-labelledby="device-list-title">
            <h2 className="device-list__sr-only" id="device-list-title">Device List</h2>

            <div className="device-list__toolbar">
              <button className="device-list__add-button" type="button" onClick={handleAddDevice}>
                <AddIcon aria-hidden="true" />
                Add Device
              </button>
            </div>

            <div className="device-list__table-shell">
              <table className="device-list__table">
                <thead>
                  <tr>
                    <th scope="col">Device ID</th>
                    <th scope="col">Power Import</th>
                    <th scope="col">Power Export</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deviceRows.length > 0 ? (
                    deviceRows.map((device) => (
                      <tr key={device.deviceId}>
                        <td>
                          <Link to={`/dashboard/${encodeURIComponent(device.deviceId)}`}>
                            {device.deviceId}
                          </Link>
                        </td>
                        <td>{formatPower(device.powerImport)}</td>
                        <td>{formatPower(device.powerExport)}</td>
                        <td>
                          <div className="device-list__actions">
                            <button type="button" aria-label={`Open logs for ${device.deviceId}`} title="Logs" onClick={() => handleOpenLogs(device.deviceId)}>
                              <FormatListBulletedIcon aria-hidden="true" />
                            </button>
                            <button type="button" aria-label={`Open controls for ${device.deviceId}`} title="Controls" onClick={() => handleOpenControls(device.deviceId)}>
                              <TuneIcon aria-hidden="true" />
                            </button>
                            <button type="button" aria-label={`Open settings for ${device.deviceId}`} title="Settings" onClick={() => handleOpenSettings(device.deviceId)}>
                              <SettingsIcon aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="device-list__empty" colSpan="4">
                        {isLoading ? 'Loading devices...' : 'No devices found.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {(isLoading || loadError) && (
              <p className="device-list__status" role="status">
                {isLoading ? 'Loading devices...' : loadError}
              </p>
            )}
          </section>
        </div>

        <DeviceLogsModal deviceId={logsDeviceId} isOpen={Boolean(logsDeviceId)} onClose={() => setLogsDeviceId('')} />
        <DeviceControlsModal deviceId={controlsDeviceId} isOpen={Boolean(controlsDeviceId)} onClose={() => setControlsDeviceId('')} />
        <DeviceSettingsModal deviceId={settingsDeviceId} isOpen={Boolean(settingsDeviceId)} onClose={() => setSettingsDeviceId('')} />
        {isAddDeviceOpen ? (
          <div
            className="device-list__tech-modal"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                handleCloseAddDevice()
              }
            }}
          >
            <section
              className="device-list__tech-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="device-list-tech-title"
            >
              <header className="device-list__tech-header">
                <div>
                  <h2 id="device-list-tech-title">Add Device</h2>
                  <p>
                    {isTechnicianAuthorized
                      ? `Connect and configure the board for ${customerAssignment?.displayName || 'this customer'}.`
                      : 'Enter technician credentials to continue.'}
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Close add device"
                  onClick={handleCloseAddDevice}
                  ref={addDeviceCloseRef}
                >
                  <CloseIcon aria-hidden="true" />
                </button>
              </header>
              <div className="device-list__tech-body">
                {isTechnicianAuthorized ? (
                  <TechConfigPage
                    embedded
                    isDarkMode={isDarkMode}
                    onDeviceAssigned={handleDeviceAssigned}
                  />
                ) : (
                  <form className="device-list__tech-auth" onSubmit={handleTechnicianAuthorization}>
                    <div className="device-list__tech-auth-intro">
                      <span>Technician authorization</span>
                      <h3>Verify access before connecting a customer device.</h3>
                    </div>

                    <label>
                      <span>Email address</span>
                      <div className="device-list__tech-auth-field">
                        <EmailIcon aria-hidden="true" />
                        <input
                          type="email"
                          name="email"
                          autoComplete="username"
                          value={technicianCredentials.email}
                          onChange={handleTechnicianCredentialChange}
                          required
                          autoFocus
                        />
                      </div>
                    </label>

                    <label>
                      <span>Password</span>
                      <div className="device-list__tech-auth-field">
                        <LockIcon aria-hidden="true" />
                        <input
                          type={isTechnicianPasswordVisible ? 'text' : 'password'}
                          name="password"
                          autoComplete="current-password"
                          value={technicianCredentials.password}
                          onChange={handleTechnicianCredentialChange}
                          required
                        />
                        <button
                          type="button"
                          aria-label={isTechnicianPasswordVisible ? 'Hide password' : 'Show password'}
                          onClick={() => setIsTechnicianPasswordVisible((current) => !current)}
                        >
                          {isTechnicianPasswordVisible
                            ? <VisibilityOffIcon aria-hidden="true" />
                            : <VisibilityIcon aria-hidden="true" />}
                        </button>
                      </div>
                    </label>

                    {technicianAuthError ? (
                      <p className="device-list__tech-auth-error" role="alert">
                        {technicianAuthError}
                      </p>
                    ) : null}

                    <div className="device-list__tech-auth-actions">
                      <button type="button" onClick={handleCloseAddDevice}>
                        Cancel
                      </button>
                      <button type="submit" disabled={isAuthorizingTechnician}>
                        {isAuthorizingTechnician ? 'Verifying...' : 'Proceed'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </section>
          </div>
        ) : null}
      </section>
    </main>
  )
}

export default DeviceListPage
